import { z } from 'zod';

import { defaultWhenNullish } from 'utility/zod';

export const BookingStateEnum = z.enum([
  'Active',
  'Canceled',
  'Deleted',
  'Pending',
  'Noshow',
  'Seated'
]);
export type BookingState = z.infer<typeof BookingStateEnum>;

export const RESERVATION = 'Reservation';
export const WALKIN = 'Walkin';
export const WAITPARTY = 'Waitparty';
export const PARTY_SIZE = 'PartySize';
export const NONE = 'None';
export const CARD_ON_FILE = 'CardOnFile';
export const PAYMENT = 'Payment';

export const BookingTypeEnum = z.enum([RESERVATION, WALKIN, WAITPARTY]);
export type BookingType = z.infer<typeof BookingTypeEnum>;

export const FeeCalcTypeEnum = z.enum([PARTY_SIZE, RESERVATION, NONE]);
export type FeeCalcType = z.infer<typeof FeeCalcTypeEnum>;

export const FeeCollectTypeEnum = z.enum([CARD_ON_FILE, PAYMENT, NONE]);
export type FeeCollectType = z.infer<typeof FeeCollectTypeEnum>;

export const CalcTypeTransform = defaultWhenNullish(z.string(), '').transform(
  (calcType): FeeCalcType => {
    switch (calcType) {
      case PARTY_SIZE:
        return FeeCalcTypeEnum.enum.PartySize;
      case RESERVATION:
        return FeeCalcTypeEnum.enum.Reservation;
      default:
        return FeeCalcTypeEnum.enum.None;
    }
  }
);

export const CollectTypeTransform = defaultWhenNullish(
  z.string(),
  ''
).transform((collectType): FeeCollectType => {
  switch (collectType) {
    case CARD_ON_FILE:
      return FeeCollectTypeEnum.enum.CardOnFile;
    case PAYMENT:
      return FeeCollectTypeEnum.enum.Payment;
    default:
      return FeeCollectTypeEnum.enum.None;
  }
});

export const Fee = z.object({
  calcType: CalcTypeTransform,
  collectType: CollectTypeTransform,
  fee: defaultWhenNullish(z.number(), 0),
  shouldCollect: defaultWhenNullish(z.boolean(), false),
  cancelDeadlineMinutes: defaultWhenNullish(z.number(), 0)
});
export type Fee = z.infer<typeof Fee>;

const Booking = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  phoneCode: z.string(),
  dateAndTime: z.string(),
  partySize: z.number(),
  note: z.string(),
  bookingId: z.string(),
  state: BookingStateEnum,
  tags: z.array(z.string()),
  dietaryTags: defaultWhenNullish(z.array(z.string()), []),
  optInMarketingEmail: z.boolean(),
  optInMarketingSms: z.boolean(),
  hasGuestConfirmed: z.boolean(),
  positionInQueue: defaultWhenNullish(z.number(), 0),
  type: defaultWhenNullish(BookingTypeEnum, BookingTypeEnum.enum.Reservation),
  externalReservationUrl: defaultWhenNullish(z.string(), ''),
  showSentEmailConfirmation: defaultWhenNullish(z.boolean(), true)
});
export type Booking = z.infer<typeof Booking>;

export const BookingResponseSchema = z.object({
  booking: Booking,
  fee: Fee.nullish()
});
export type NextableBookingResponse = z.infer<typeof BookingResponseSchema>;

const PaymentMethod = z.object({
  card_type: z.string(),
  card_last4: z.string(),
  card_expiration_month: z.number(),
  card_expiration_year: z.number()
});
export type PaymentMethod = z.infer<typeof PaymentMethod>;

const SavedPaymentMethod = z.object({
  id: z
    .number()
    .or(z.string())
    .transform(it => it.toString()),
  payment_method: PaymentMethod
});
export type SavedPaymentMethod = z.infer<typeof SavedPaymentMethod>;

export const CardOnFile = z.object({
  opt_in_checkless_payments: z.boolean(),
  tip_percentage: z.number(),
  saved_payment_method: SavedPaymentMethod.nullish()
});
export type CardOnFile = z.infer<typeof CardOnFile>;

export const BookingResponse = z.object({
  booking: Booking,
  fee: Fee.nullish(),
  card_on_file: CardOnFile.nullish()
});
export type BookingResponse = z.infer<typeof BookingResponse>;

export type HandleSuccessOptions = {
  navUrl?: string;
};

export type HandleCreateBookingSuccessOptions = {
  navUrl?: string;
  booking: Booking;
};

export type HandleUpdateBookingSuccessOptions = {
  navUrl?: string;
  booking: Booking;
};

export type HandleErrorOptions = {
  navUrl?: string;
  code?: number;
  forceReload?: boolean;
};
